import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Typography, CircularProgress, Paper, Alert, Button } from '@mui/material';
import { Navigate } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import mermaid from 'mermaid';

mermaid.initialize({ startOnLoad: false, theme: 'default', securityLevel: 'loose' });

let mermaidCounter = 0;
function MermaidDiagram({ chart }) {
  const ref = useRef(null);
  useEffect(() => {
    const id = 'mermaid-' + (++mermaidCounter);
    if (ref.current) {
      mermaid.render(id, chart).then(({ svg }) => {
        if (ref.current) ref.current.innerHTML = svg;
      }).catch(err => {
        if (ref.current) ref.current.innerHTML = '<pre style=\"color:#c62828\">Diagram error: ' + err.message + '</pre>';
      });
    }
  }, [chart]);
  return <Box ref={ref} sx={{ my: 2, overflow: 'auto', '& svg': { maxWidth: '100%' } }} />;
}

export default function AdminArchitecture() {
  const { user } = useAuth();
  const [md, setMd] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError('');
    fetch('/docs/yaplar-architecture.md?ts=' + Date.now())
      .then(r => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const lm = r.headers.get('last-modified');
        if (lm) setLastUpdated(new Date(lm));
        return r.text();
      })
      .then(t => { setMd(t); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user && user.role !== 'admin') return <Navigate to="/dashboard" />;

  return (
    <Layout>
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e' }}>System architecture</Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Internal documentation. {lastUpdated && 'Last updated ' + lastUpdated.toLocaleDateString()}
            </Typography>
          </Box>
          <Button startIcon={<RefreshIcon />} onClick={load} variant="outlined" size="small">Refresh</Button>
        </Box>

        {loading && <Box sx={{ display:'flex', justifyContent:'center', py:8 }}><CircularProgress /></Box>}
        {error && <Alert severity="error">Failed to load architecture doc: {error}</Alert>}

        {!loading && !error && (
          <Paper sx={{ p: { xs: 2, md: 4 }, '& h1': { mt: 3, fontSize: 28, color: '#1a237e' }, '& h2': { mt: 3, fontSize: 22, color: '#1a237e', borderBottom: '1px solid #e0e0e0', pb: 1 }, '& h3': { mt: 2, fontSize: 18 }, '& h4': { mt: 2, fontSize: 16 }, '& table': { borderCollapse: 'collapse', width: '100%', my: 2, fontSize: 14 }, '& th, & td': { border: '1px solid #e0e0e0', p: 1, textAlign: 'left' }, '& th': { background: '#f5f6fa' }, '& code': { background: '#f5f6fa', px: 0.5, py: 0.2, borderRadius: 0.5, fontFamily: 'monospace', fontSize: '0.9em' }, '& pre': { background: '#1a237e', color: 'white', p: 2, borderRadius: 1, overflow: 'auto', '& code': { background: 'transparent', color: 'white', p: 0 } }, '& blockquote': { borderLeft: '4px solid #ff6f00', pl: 2, color: '#666', my: 2 } }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                code({ node, inline, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || '');
                  const lang = match && match[1];
                  const content = String(children).replace(/\n$/, '');
                  if (!inline && lang === 'mermaid') return <MermaidDiagram chart={content} />;
                  return <code className={className} {...props}>{children}</code>;
                }
              }}
            >{md}</ReactMarkdown>
          </Paper>
        )}
      </Box>
    </Layout>
  );
}
